import '../../../shared/StyledInput.css';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { PAGE_SIZE_OPTIONS, invalidateDefaultPageSize } from '../../../shared/pagination';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import CancelButton from '../../../shared/CancelButton';
import SaveButton from '../../../shared/SaveButton';
import Accordion from '../../../shared/Accordion';
import CopyButton from '../../../shared/CopyButton';
import ToggleSwitch from '../../../shared/ToggleSwitch';
import { usePermissions } from '../../../shared/PermissionsContext';
import { clearShipmentWeightUnitCache } from '../../../shared/useShipmentWeightUnit';
import { askConfirm } from '../../../shared/AppDialogs';

// The rebuild endpoint is one synchronous request with no incremental
// progress to report (it just runs the whole library server-side and
// returns when done), so this is a spinner rather than a real N-of-M
// progress bar — accurate to what we actually know while waiting.
const SPIN_KEYFRAMES = '@keyframes platform-settings-spin { to { transform: rotate(360deg); } }';

const WEIGHT_UNIT_OPTIONS = [
  { value: 'g', label: 'Grams (g)' },
  { value: 'kg', label: 'Kilograms (kg)' },
  { value: 'oz', label: 'Ounces (oz)' },
  { value: 'lb', label: 'Pounds (lb)' },
];
const DIMENSION_UNIT_OPTIONS = [
  { value: 'mm', label: 'Millimetres (mm)' },
  { value: 'cm', label: 'Centimetres (cm)' },
  { value: 'm', label: 'Metres (m)' },
  { value: 'in', label: 'Inches (in)' },
];
const SHIPMENT_WEIGHT_UNIT_OPTIONS = [
  { value: 'kg', label: 'Kilograms (kg)' },
  { value: 'lb', label: 'Pounds (lb)' },
];
const SHIPMENT_DIMENSION_UNIT_OPTIONS = [
  { value: 'cm', label: 'Centimetres (cm)' },
  { value: 'in', label: 'Inches (in)' },
];

// Home for tenant-wide settings that don't belong to any one business
// module (unlike Company Information, which already has its own dedicated
// screen). Grouped here as they're discovered rather than each getting a
// bespoke settings screen of its own. Grouped into collapsible Accordion
// cards (default collapsed) now that the list has grown past one flat form.
function PlatformSettingsPage({ onNavigate }) {
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [defaultPageSize, setDefaultPageSize] = useState(25);
  const [savedPageSize, setSavedPageSize] = useState(25);
  const [thumbSize, setThumbSize] = useState(200);
  const [savedThumbSize, setSavedThumbSize] = useState(200);
  const [previewSize, setPreviewSize] = useState(800);
  const [savedPreviewSize, setSavedPreviewSize] = useState(800);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [regenerating, setRegenerating] = useState(false);
  const [regenerateResult, setRegenerateResult] = useState('');

  // Weight & Dimensions (issue #100) — each locked permanently the first
  // time it's saved (no unit conversion exists anywhere in the system, so
  // changing either later would silently mislabel every value already
  // entered against a Product UoM). savedWeightUnit/savedDimensionUnit
  // being truthy is what "locked" means — once set, the picker below
  // renders as static text instead of a `<select>`.
  const [weightUnit, setWeightUnit] = useState('');
  const [savedWeightUnit, setSavedWeightUnit] = useState('');
  const [dimensionUnit, setDimensionUnit] = useState('');
  const [savedDimensionUnit, setSavedDimensionUnit] = useState('');
  const [shipmentWeightUnit, setShipmentWeightUnit] = useState('');
  const [savedShipmentWeightUnit, setSavedShipmentWeightUnit] = useState('');
  const [shipmentDimensionUnit, setShipmentDimensionUnit] = useState('');
  const [savedShipmentDimensionUnit, setSavedShipmentDimensionUnit] = useState('');
  const [unitsSaving, setUnitsSaving] = useState(false);
  const [unitsSuccess, setUnitsSuccess] = useState('');
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [savedTestEmailAddress, setSavedTestEmailAddress] = useState('');
  const [idleTimeoutHours, setIdleTimeoutHours] = useState(8);
  const [savedIdleTimeoutHours, setSavedIdleTimeoutHours] = useState(8);
  const [portalName, setPortalName] = useState('');
  const [savedPortalName, setSavedPortalName] = useState('');
  const [portalUrlPrefix, setPortalUrlPrefix] = useState('');
  const [showPortalOnWebsite, setShowPortalOnWebsite] = useState(false);
  const [savedShowPortalOnWebsite, setSavedShowPortalOnWebsite] = useState(false);
  const { isAdmin } = usePermissions();

  useEffect(() => {
    axios.get('/api/platform-settings')
      .then((res) => {
        const loaded = res.data.data?.default_page_size ?? 25;
        setDefaultPageSize(loaded);
        setSavedPageSize(loaded);
        const loadedThumbSize = res.data.data?.media_thumb_size_px ?? 200;
        setThumbSize(loadedThumbSize);
        setSavedThumbSize(loadedThumbSize);
        const loadedPreviewSize = res.data.data?.media_preview_size_px ?? 800;
        setPreviewSize(loadedPreviewSize);
        setSavedPreviewSize(loadedPreviewSize);
        const loadedWeightUnit = res.data.data?.weight_unit || '';
        setWeightUnit(loadedWeightUnit);
        setSavedWeightUnit(loadedWeightUnit);
        const loadedDimensionUnit = res.data.data?.dimension_unit || '';
        setDimensionUnit(loadedDimensionUnit);
        setSavedDimensionUnit(loadedDimensionUnit);
        const loadedShipmentWeightUnit = res.data.data?.shipment_weight_unit || '';
        setShipmentWeightUnit(loadedShipmentWeightUnit);
        setSavedShipmentWeightUnit(loadedShipmentWeightUnit);
        const loadedShipmentDimensionUnit = res.data.data?.shipment_dimension_unit || '';
        setShipmentDimensionUnit(loadedShipmentDimensionUnit);
        setSavedShipmentDimensionUnit(loadedShipmentDimensionUnit);
        const loadedTestEmailAddress = res.data.data?.test_email_address || '';
        setTestEmailAddress(loadedTestEmailAddress);
        setSavedTestEmailAddress(loadedTestEmailAddress);
        const loadedIdleTimeout = res.data.data?.staff_idle_timeout_hours ?? 8;
        setIdleTimeoutHours(loadedIdleTimeout);
        setSavedIdleTimeoutHours(loadedIdleTimeout);
        const loadedPortalName = res.data.data?.portal_name || '';
        setPortalName(loadedPortalName);
        setSavedPortalName(loadedPortalName);
        setPortalUrlPrefix(res.data.data?.portal_url_prefix || '');
        const loadedShowPortal = !!res.data.data?.show_portal_on_website;
        setShowPortalOnWebsite(loadedShowPortal);
        setSavedShowPortalOnWebsite(loadedShowPortal);
      })
      .catch((err) => showError(err, 'Failed to load Platform Settings'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    const trimmedPortalName = portalName.trim().toLowerCase();
    if (trimmedPortalName !== savedPortalName && !(await askConfirm(
      `Change the customer portal address to ${portalUrlPrefix}${trimmedPortalName}?

`
      + `The old address (${portalUrlPrefix}${savedPortalName}) stops working straight away, so Customers' bookmarks `
      + 'and the address in invite emails already sent no longer lead to the portal. Customers who are signed in need to sign in again at the new address.',
      { confirmLabel: 'Change Address' },
    ))) return;
    setSaving(true);
    setSuccess('');
    try {
      await axios.put('/api/platform-settings', {
        default_page_size: Number(defaultPageSize),
        media_thumb_size_px: Number(thumbSize),
        media_preview_size_px: Number(previewSize),
        test_email_address: testEmailAddress,
        staff_idle_timeout_hours: Number(idleTimeoutHours),
        portal_name: trimmedPortalName,
        // Admin only (issue #288), so only an Admin's save sends it.
        ...(isAdmin ? { show_portal_on_website: showPortalOnWebsite } : {}),
      });
      setSavedShowPortalOnWebsite(showPortalOnWebsite);
      setPortalName(trimmedPortalName);
      setSavedPortalName(trimmedPortalName);
      setSavedPageSize(defaultPageSize);
      setSavedThumbSize(thumbSize);
      setSavedPreviewSize(previewSize);
      setSavedTestEmailAddress(testEmailAddress);
      setSavedIdleTimeoutHours(idleTimeoutHours);
      invalidateDefaultPageSize();
      setSuccess('Platform settings saved.');
      dismissError();
    } catch (err) {
      showError(err, 'Save failed');
    }
    setSaving(false);
  };

  const handleCancel = () => {
    setDefaultPageSize(savedPageSize);
    setThumbSize(savedThumbSize);
    setPreviewSize(savedPreviewSize);
    setTestEmailAddress(savedTestEmailAddress);
    setIdleTimeoutHours(savedIdleTimeoutHours);
    setPortalName(savedPortalName);
    setShowPortalOnWebsite(savedShowPortalOnWebsite);
    setSuccess('');
    if (onNavigate) onNavigate('global-dashboard');
  };

  // Independent of Save — regenerates every image's thumb/preview files
  // using whatever sizes are currently saved on the server (not any
  // unsaved edit in the fields above, hence the "Save first" hint below).
  const handleRegenerate = async () => {
    setRegenerating(true);
    setRegenerateResult('');
    try {
      const res = await axios.post('/api/media-images/rebuild-thumbnails');
      setRegenerateResult(res.data.message || 'Done.');
      dismissError();
    } catch (err) {
      showError(err, 'Regenerate failed');
    }
    setRegenerating(false);
  };

  // Separate from the main Save button on purpose — this is a one-way lock,
  // not a routine edit, so it gets its own explicit action and a warning
  // rather than sliding through silently with every other field on Save.
  // Only sends whichever of the two isn't already locked.
  const handleSaveUnits = async () => {
    const changes = [];
    if (!savedWeightUnit && weightUnit) changes.push(`Product UoM Weight Unit → ${WEIGHT_UNIT_OPTIONS.find((o) => o.value === weightUnit)?.label}`);
    if (!savedDimensionUnit && dimensionUnit) changes.push(`Product UoM Dimension Unit → ${DIMENSION_UNIT_OPTIONS.find((o) => o.value === dimensionUnit)?.label}`);
    if (!savedShipmentWeightUnit && shipmentWeightUnit) changes.push(`Shipment Weight Unit → ${SHIPMENT_WEIGHT_UNIT_OPTIONS.find((o) => o.value === shipmentWeightUnit)?.label}`);
    if (!savedShipmentDimensionUnit && shipmentDimensionUnit) changes.push(`Shipment Dimension Unit → ${SHIPMENT_DIMENSION_UNIT_OPTIONS.find((o) => o.value === shipmentDimensionUnit)?.label}`);
    if (!changes.length) return;
    if (!(await askConfirm(`This will permanently lock in:\n\n${changes.join('\n')}\n\nThis cannot be changed afterward. Continue?`, { confirmLabel: 'Lock In', destructive: true }))) return;

    setUnitsSaving(true);
    setUnitsSuccess('');
    try {
      const payload = {};
      if (!savedWeightUnit && weightUnit) payload.weight_unit = weightUnit;
      if (!savedDimensionUnit && dimensionUnit) payload.dimension_unit = dimensionUnit;
      if (!savedShipmentWeightUnit && shipmentWeightUnit) payload.shipment_weight_unit = shipmentWeightUnit;
      if (!savedShipmentDimensionUnit && shipmentDimensionUnit) payload.shipment_dimension_unit = shipmentDimensionUnit;
      await axios.put('/api/platform-settings', payload);
      if (payload.weight_unit) setSavedWeightUnit(payload.weight_unit);
      if (payload.dimension_unit) setSavedDimensionUnit(payload.dimension_unit);
      if (payload.shipment_weight_unit) setSavedShipmentWeightUnit(payload.shipment_weight_unit);
      if (payload.shipment_dimension_unit) setSavedShipmentDimensionUnit(payload.shipment_dimension_unit);
      if (payload.shipment_weight_unit || payload.shipment_dimension_unit) clearShipmentWeightUnitCache();
      setUnitsSuccess('Locked in.');
      dismissError();
    } catch (err) {
      showError(err, 'Could not save Weight & Dimension Units');
    }
    setUnitsSaving(false);
  };

  if (loading) {
    return <div style={{ width: '100%', maxWidth: 600, margin: '32px auto' }}>Loading...</div>;
  }

  return (
    // width: 100% is required alongside maxWidth here — .main-content is a
    // column flex container, and this div's auto left/right margins (for
    // centering) disable flexbox's default cross-axis stretch, so without
    // an explicit width it shrinks-to-fit its content instead of filling
    // to the maxWidth cap. That made the card visibly narrower whenever an
    // Accordion section was collapsed (short content) than when one was
    // open (wider content) — a width "jump" on every expand/collapse.
    <div style={{ width: '100%', maxWidth: 600, margin: '32px auto 48px', background: '#f6f8fa', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: 24 }}>
      <h2 style={{ margin: '0 0 4px', fontWeight: 600, fontSize: 20 }}>Platform Settings</h2>
      <p style={{ color: '#666', fontSize: 14, marginTop: 0 }}>Tenant-wide defaults not specific to any one module.</p>

      <form onSubmit={handleSave} className="platform-settings-form" style={{ marginTop: 16 }}>
        <Accordion title="Display Defaults" defaultOpen={false}>
          <label>Default Rows Per Page</label><br />
          <select className="styled-input"
            value={defaultPageSize}
            onChange={(e) => setDefaultPageSize(Number(e.target.value))}
            style={{ width: 220 }}
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>{size} per page</option>
            ))}
          </select>
          <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
            Applied as the starting page size on list screens. Interim system-wide default until
            per-user preferences exist — always changeable per screen after that.
          </div>
        </Accordion>

        <Accordion title="Media Sizes" defaultOpen={false}>
          <label>Thumbnail Size (px)</label>
          <input
            type="number"
            min={50}
            max={500}
            value={thumbSize}
            onChange={(e) => setThumbSize(e.target.value)}
            style={{ width: 220, padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, display: 'block', marginTop: 4 }}
          />
          <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
            Longest edge, in pixels, for the small image shown in list/grid rows
            (Products list, Media Library, Product Media). Smaller means faster
            page loads.
          </div>

          <label style={{ display: 'block', marginTop: 16 }}>Preview Size (px)</label>
          <input
            type="number"
            min={200}
            max={2000}
            value={previewSize}
            onChange={(e) => setPreviewSize(e.target.value)}
            style={{ width: 220, padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14 }}
          />
          <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
            Longest edge, in pixels, for the larger image on the Product edit
            form and the Pricelist Extended (Catalog) Report.
          </div>

          <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #e3e7ee' }}>
            <div style={{ fontWeight: 600, fontSize: 13 }}>Regenerate Thumbnails</div>
            <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
              Re-derives every image's thumbnail and preview at the sizes saved
              above. New/never-viewed images generate automatically on first
              view — use this after changing the sizes above, or to recover a
              corrupted derivative. Save any size change first; this uses what's
              currently saved, not an unsaved edit.
            </div>
            <style>{SPIN_KEYFRAMES}</style>
            <button
              type="button"
              onClick={handleRegenerate}
              disabled={regenerating}
              style={{ marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 8, padding: '7px 14px', borderRadius: 6, border: 'none', background: regenerating ? '#90caf9' : '#1976d2', color: '#fff', fontWeight: 600, fontSize: 13, cursor: regenerating ? 'not-allowed' : 'pointer' }}
            >
              {regenerating && <Loader2 size={15} strokeWidth={2.5} style={{ animation: 'platform-settings-spin 0.8s linear infinite' }} />}
              {regenerating ? 'Regenerating...' : 'Regenerate Thumbnails Now'}
            </button>
            {regenerateResult && <div style={{ color: '#2e7d32', marginTop: 10, fontSize: 13 }}>{regenerateResult}</div>}
          </div>
        </Accordion>

        <Accordion title="Weight & Dimension Units" defaultOpen={false}>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>
            The Weight/Length/Width/Height of each Product UoM (such as a box
            of 12, or a reel) is in the Product UoM units below, and every
            weight and Length/Width/Height entered on a shipment is in the
            Shipment units. Changing a unit later
            would mislabel every value already entered, so each can only be
            set once — pick carefully.
          </div>

          <label>Product UoM Weight Unit</label><br />
          {savedWeightUnit ? (
            <div style={{ fontSize: 14, marginTop: 4 }}>
              {WEIGHT_UNIT_OPTIONS.find((o) => o.value === savedWeightUnit)?.label}
              <span style={{ marginLeft: 8, fontSize: 12, color: '#888' }}>Locked — can't be changed.</span>
            </div>
          ) : (
            <select className="styled-input" value={weightUnit} onChange={(e) => setWeightUnit(e.target.value)} style={{ width: 220 }}>
              <option value="">(select)</option>
              {WEIGHT_UNIT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          )}

          <div style={{ marginTop: 16 }} />
          <label>Product UoM Dimension Unit</label><br />
          {savedDimensionUnit ? (
            <div style={{ fontSize: 14, marginTop: 4 }}>
              {DIMENSION_UNIT_OPTIONS.find((o) => o.value === savedDimensionUnit)?.label}
              <span style={{ marginLeft: 8, fontSize: 12, color: '#888' }}>Locked — can't be changed.</span>
            </div>
          ) : (
            <select className="styled-input" value={dimensionUnit} onChange={(e) => setDimensionUnit(e.target.value)} style={{ width: 220 }}>
              <option value="">(select)</option>
              {DIMENSION_UNIT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          )}

          <div style={{ marginTop: 16 }} />
          <label>Shipment Weight Unit</label><br />
          {savedShipmentWeightUnit ? (
            <div style={{ fontSize: 14, marginTop: 4 }}>
              {SHIPMENT_WEIGHT_UNIT_OPTIONS.find((o) => o.value === savedShipmentWeightUnit)?.label}
              <span style={{ marginLeft: 8, fontSize: 12, color: '#888' }}>Locked — can't be changed.</span>
            </div>
          ) : (
            <select className="styled-input" value={shipmentWeightUnit} onChange={(e) => setShipmentWeightUnit(e.target.value)} style={{ width: 220 }}>
              <option value="">(select)</option>
              {SHIPMENT_WEIGHT_UNIT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          )}

          <div style={{ marginTop: 16 }} />
          <label>Shipment Dimension Unit</label><br />
          {savedShipmentDimensionUnit ? (
            <div style={{ fontSize: 14, marginTop: 4 }}>
              {SHIPMENT_DIMENSION_UNIT_OPTIONS.find((o) => o.value === savedShipmentDimensionUnit)?.label}
              <span style={{ marginLeft: 8, fontSize: 12, color: '#888' }}>Locked — can't be changed.</span>
            </div>
          ) : (
            <select className="styled-input" value={shipmentDimensionUnit} onChange={(e) => setShipmentDimensionUnit(e.target.value)} style={{ width: 220 }}>
              <option value="">(select)</option>
              {SHIPMENT_DIMENSION_UNIT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          )}

          {(!savedWeightUnit || !savedDimensionUnit || !savedShipmentWeightUnit || !savedShipmentDimensionUnit) && (
            <div style={{ marginTop: 16 }}>
              <SaveButton
                type="button"
                loading={unitsSaving}
                disabled={(savedWeightUnit || !weightUnit) && (savedDimensionUnit || !dimensionUnit) && (savedShipmentWeightUnit || !shipmentWeightUnit) && (savedShipmentDimensionUnit || !shipmentDimensionUnit)}
                onClick={handleSaveUnits}
              >
                Lock In
              </SaveButton>
            </div>
          )}
          {unitsSuccess && <div style={{ color: '#2e7d32', marginTop: 10, fontSize: 13 }}>{unitsSuccess}</div>}
        </Accordion>

        <Accordion title="Outbound Email" defaultOpen={false}>
          <label>Test Email Address</label><br />
          <input
            type="email"
            value={testEmailAddress}
            onChange={(e) => setTestEmailAddress(e.target.value)}
            placeholder="dev-test@example.com"
            className="styled-input"
            style={{ width: 320 }}
          />
          <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
            Outside production, every templated email sent from a Commercial
            Entity's Activity view is redirected here regardless of the
            recipient picked — subject, body, and attachments are unchanged.
            Sending is refused in non-production until this is set.
          </div>
        </Accordion>

        <Accordion title="Staff Sign-in" defaultOpen={false}>
          <label>Sign Out After Inactivity (hours)</label><br />
          <input
            type="number"
            min={1}
            max={720}
            step={1}
            value={idleTimeoutHours}
            onChange={(e) => setIdleTimeoutHours(e.target.value)}
            className="styled-input"
            style={{ width: 120 }}
          />
          <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
            A signed-in staff member or Customer is signed out after this
            many hours without using Optim or the customer portal. Everyone
            is also signed out 30 days after signing in, however active they
            are.
          </div>
        </Accordion>

        <Accordion title="Customer Portal" defaultOpen={false}>
          {savedPortalName && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: '#5a6b85' }}>Portal address</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ fontSize: 13, wordBreak: 'break-all' }}>{portalUrlPrefix}{savedPortalName}</span>
                <CopyButton text={`${portalUrlPrefix}${savedPortalName}`} title="Copy portal address" />
              </div>
              <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>Every Customer signs in here with their own login.</div>
            </div>
          )}
          <label>Portal Address Name</label><br />
          <input
            type="text"
            value={portalName}
            onChange={(e) => setPortalName(e.target.value)}
            maxLength={40}
            className="styled-input"
            style={{ width: 320 }}
          />
          <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
            Customers sign in at{' '}
            <strong style={{ wordBreak: 'break-all' }}>{portalUrlPrefix}{portalName.trim().toLowerCase() || '…'}</strong>.
            Lower-case letters, digits and hyphens, 3 to 40 characters. Changing it
            stops the old address working.
          </div>
          <div style={{ marginTop: 16 }}>
            <ToggleSwitch
              checked={showPortalOnWebsite}
              onChange={setShowPortalOnWebsite}
              disabled={!isAdmin}
              title={isAdmin ? undefined : 'Only an Admin can change this'}
              label="Show Customer portal on website"
            />
            <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
              Shows the Customer portal buttons on the website&apos;s header, home page,
              Contact page and footer. Customers&apos; own portal links work either way.
              Only an Admin can change this.
            </div>
          </div>
        </Accordion>

        {success && <div style={{ color: '#2e7d32', marginTop: 10 }}>{success}</div>}

        <div style={{ marginTop: 16 }}>
          <SaveButton loading={saving} />
          <CancelButton onClick={handleCancel} disabled={saving} style={{ marginLeft: 8 }} />
        </div>
      </form>
    </div>
  );
}

export default PlatformSettingsPage;
